import React, { useState } from 'react';

import { ButtonProps, ButtonPropsDefaults, ButtonPropsSchema } from '@usewaypoint/block-button';
import { MousePointerClick, Palette } from 'lucide-react';

import BaseSidebarPanel, { SectionHeader } from './helpers/BaseSidebarPanel';
import ColorInput from './helpers/inputs/ColorInput';
import RadioGroupInput from './helpers/inputs/RadioGroupInput';
import TextInput from './helpers/inputs/TextInput';
import MultiStylePropertyPanel from './helpers/style-inputs/MultiStylePropertyPanel';
import { Separator } from '@/components/ui/separator';
import { ToggleGroupItem } from '@/components/ui/toggle-group';

type ButtonSidebarPanelProps = {
  data: ButtonProps;
  setData: (v: ButtonProps) => void;
};

export default function ButtonSidebarPanel({ data, setData }: ButtonSidebarPanelProps) {
  const [, setErrors] = useState<Zod.ZodError | null>(null);

  const updateData = (d: unknown) => {
    const res = ButtonPropsSchema.safeParse(d);
    if (res.success) {
      setData(res.data);
      setErrors(null);
    } else {
      setErrors(res.error);
    }
  };

  const text = data.props?.text ?? ButtonPropsDefaults.text;
  const url = data.props?.url ?? ButtonPropsDefaults.url;
  const fullWidth = data.props?.fullWidth ?? ButtonPropsDefaults.fullWidth;
  const size = data.props?.size ?? ButtonPropsDefaults.size;
  const buttonStyle = data.props?.buttonStyle ?? ButtonPropsDefaults.buttonStyle;
  const buttonTextColor = data.props?.buttonTextColor ?? ButtonPropsDefaults.buttonTextColor;
  const buttonBackgroundColor = data.props?.buttonBackgroundColor ?? ButtonPropsDefaults.buttonBackgroundColor;

  return (
    <BaseSidebarPanel title="Button block">
      {/* CONTENT SECTION */}
      <div className="space-y-4">
        <SectionHeader icon={MousePointerClick}>Content</SectionHeader>
        <div className="space-y-3">
          <TextInput
            label="Text"
            defaultValue={text}
            onChange={(text) => updateData({ ...data, props: { ...data.props, text } })}
          />
          <TextInput
            label="URL"
            defaultValue={url}
            onChange={(url) => updateData({ ...data, props: { ...data.props, url } })}
          />
        </div>
      </div>

      <Separator className="my-6" />

      {/* APPEARANCE SECTION */}
      <div className="space-y-4">
        <SectionHeader icon={Palette}>Appearance</SectionHeader>
        <div className="space-y-3">
          <RadioGroupInput
            label="Style"
            defaultValue={buttonStyle}
            onChange={(buttonStyle) => updateData({ ...data, props: { ...data.props, buttonStyle } })}
          >
            <ToggleGroupItem value="rectangle">Rectangle</ToggleGroupItem>
            <ToggleGroupItem value="rounded">Rounded</ToggleGroupItem>
            <ToggleGroupItem value="pill">Pill</ToggleGroupItem>
          </RadioGroupInput>
          <RadioGroupInput
            label="Size"
            defaultValue={size}
            onChange={(size) => updateData({ ...data, props: { ...data.props, size } })}
          >
            <ToggleGroupItem value="x-small">Xs</ToggleGroupItem>
            <ToggleGroupItem value="small">Sm</ToggleGroupItem>
            <ToggleGroupItem value="medium">Md</ToggleGroupItem>
            <ToggleGroupItem value="large">Lg</ToggleGroupItem>
          </RadioGroupInput>
          <ColorInput
            label="Text color"
            defaultValue={buttonTextColor}
            onChange={(buttonTextColor) => updateData({ ...data, props: { ...data.props, buttonTextColor } })}
          />
          <ColorInput
            label="Button color"
            defaultValue={buttonBackgroundColor}
            onChange={(buttonBackgroundColor) => updateData({ ...data, props: { ...data.props, buttonBackgroundColor } })}
          />
        </div>
      </div>

      <Separator className="my-6" />

      {/* LAYOUT SECTION */}
      <div className="space-y-4">
        <SectionHeader>Layout</SectionHeader>
        <div className="space-y-3">
          <RadioGroupInput
            label="Width"
            defaultValue={fullWidth ? 'FULL_WIDTH' : 'AUTO'}
            onChange={(v) => updateData({ ...data, props: { ...data.props, fullWidth: v === 'FULL_WIDTH' } })}
          >
            <ToggleGroupItem value="FULL_WIDTH">Full</ToggleGroupItem>
            <ToggleGroupItem value="AUTO">Auto</ToggleGroupItem>
          </RadioGroupInput>
          <MultiStylePropertyPanel
            names={['backgroundColor', 'fontFamily', 'fontSize', 'fontWeight', 'textAlign', 'padding']}
            value={data.style}
            onChange={(style) => updateData({ ...data, style })}
          />
        </div>
      </div>
    </BaseSidebarPanel>
  );
}
